import { useState, useEffect } from "react";
import api from "@/api";
import { Shield, CheckCircle2, AlertCircle, Clock, RefreshCw, HeartPulse, Cloud, Database, KeyRound, Bell } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  ok: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "OK" },
  pending: { icon: Clock, cls: "text-amber-600 bg-amber-50 border-amber-200", label: "En attente" },
  to_run: { icon: Clock, cls: "text-amber-600 bg-amber-50 border-amber-200", label: "A executer" },
  to_confirm: { icon: Clock, cls: "text-amber-600 bg-amber-50 border-amber-200", label: "À confirmer" },
  ko: { icon: AlertCircle, cls: "text-rose-700 bg-rose-50 border-rose-200", label: "Probleme" },
  not_configured: { icon: AlertCircle, cls: "text-rose-700 bg-rose-50 border-rose-200", label: "Non configure" },
};

function StatusPill({ status, testid }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function SecurityProofsTab() {
  const [proofs, setProofs] = useState(null);
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, h, b] = await Promise.all([
        api.get("/ops/security-proofs").catch(() => ({ data: null })),
        api.get("/ops/health/deep").catch(() => ({ data: null })),
        api.get("/ops/backups").catch(() => ({ data: null })),
      ]);
      setProofs(p.data);
      setHealth(h.data);
      setBackups(b.data);
    } catch (err) {
      toast.error("Erreur de chargement ops");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const runBackupNow = async () => {
    if (!backups?.configured) {
      toast.error("Backup non configure. Verifier .env");
      return;
    }
    toast.info("Backup en cours...");
    try {
      const r = await api.post("/ops/backups/run-now");
      if (r.data.status === "success") {
        toast.success(`Backup OK : ${r.data.size_mb} MB`);
      } else {
        toast.error(`Backup ${r.data.status}: ${r.data.error || r.data.reason || "?"}`);
      }
      fetchAll();
    } catch (err) {
      toast.error("Backup error");
    }
  };

  const score = proofs?.summary?.score_pct ?? 0;
  const scoreCls = score >= 85 ? "text-emerald-600" : score >= 60 ? "text-amber-600" : "text-rose-600";
  const liveSnapshot = proofs?.snapshot;
  const blockingItems = proofs?.proofs
    ? Object.entries(proofs.proofs).filter(([, value]) => ["ko", "pending", "to_confirm"].includes(value.status)).slice(0, 4)
    : [];

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" /> Preuves de sécurité & Ops
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Métriques calculées depuis MongoDB, les variables runtime et les logs applicatifs.
          </p>
        </div>
        <button
          data-testid="ops-refresh-btn"
          onClick={fetchAll}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Rafraichir
        </button>
      </div>

      {/* SCORE CARD */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="dash-card">
          <div className="flex items-center gap-2 mb-2 text-slate-600">
            <Shield className="w-4 h-4" /> <span className="text-xs uppercase">Score securite</span>
          </div>
          <p className={`text-3xl font-bold ${scoreCls}`} data-testid="ops-score">
            {score}%
          </p>
          <p className="text-xs text-slate-500 mt-1" data-testid="ops-score-detail">
            {proofs?.summary?.ok}/{proofs?.summary?.total_checks} preuves OK · {proofs?.summary?.ko ?? 0} KO
          </p>
          <p className="text-[11px] text-slate-400 mt-1" data-testid="ops-source">Source : {proofs?.source === "mongodb_live" ? "live" : "—"}</p>
        </div>

        <div className="dash-card">
          <div className="flex items-center gap-2 mb-2 text-slate-600">
            <HeartPulse className="w-4 h-4" /> <span className="text-xs uppercase">Sante systeme</span>
          </div>
          <p className={`text-3xl font-bold ${health?.status === "healthy" ? "text-emerald-600" : "text-amber-600"}`} data-testid="ops-health-status">
            {health?.status === "healthy" ? "Sain" : "Degrade"}
          </p>
          <p className="text-xs text-slate-500 mt-1" data-testid="ops-health-detail">
            {health?.checks ? Object.keys(health.checks).length : 0} verifications
          </p>
        </div>

        <div className="dash-card">
          <div className="flex items-center gap-2 mb-2 text-slate-600">
            <Cloud className="w-4 h-4" /> <span className="text-xs uppercase">Backup cloud</span>
          </div>
          <p className={`text-3xl font-bold ${liveSnapshot?.ops?.backup?.status === "ok" ? "text-emerald-600" : "text-amber-600"}`} data-testid="ops-backup-status">
            {liveSnapshot?.ops?.backup?.status === "ok" ? "OK" : "À traiter"}
          </p>
          <p className="text-xs text-slate-500 mt-1" data-testid="ops-backup-detail">
            {backups?.recent_runs?.length ?? 0} run(s) · {backups?.configured ? "configuré" : "non configuré"}
          </p>
        </div>

        <div className="dash-card">
          <div className="flex items-center gap-2 mb-2 text-slate-600">
            <Bell className="w-4 h-4" /> <span className="text-xs uppercase">Notifications In-App</span>
          </div>
          <p className="text-3xl font-bold text-slate-800" data-testid="ops-inapp-total">
            {liveSnapshot?.metrics?.push?.unreadNotifications ?? 0}
          </p>
          <p className="text-xs text-slate-500 mt-1" data-testid="ops-inapp-detail">
            non lue(s) · {liveSnapshot?.metrics?.push?.subscriptions ?? 0} abonnement(s) PWA
          </p>
        </div>
      </div>

      {liveSnapshot && (
        <div className="dash-card border-emerald-100 bg-emerald-50/40" data-testid="ops-live-reality-panel">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Database className="w-4 h-4 text-emerald-600" /> Réalité opérationnelle live
            </h3>
            <span className="text-xs text-slate-500" data-testid="ops-live-generated-at">
              {new Date(liveSnapshot.generatedAt).toLocaleString("fr-FR")}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <LiveFact label="Patients actifs" value={liveSnapshot.metrics?.patients?.active ?? 0} testid="ops-live-patients" />
            <LiveFact label="Alertes ouvertes" value={liveSnapshot.metrics?.alerts?.open ?? 0} detail={`${liveSnapshot.metrics?.alerts?.openCritical ?? 0} critiques`} testid="ops-live-alerts" />
            <LiveFact label="Documents générés" value={liveSnapshot.metrics?.documents?.generated ?? 0} detail={`${liveSnapshot.metrics?.documents?.generatedLast7d ?? 0} sur 7j`} testid="ops-live-documents" />
            <LiveFact label="RWE 7j" value={liveSnapshot.metrics?.rwe?.eventsLast7d ?? 0} detail={`${liveSnapshot.metrics?.rwe?.eventsTotal ?? 0} total`} testid="ops-live-rwe" />
          </div>
        </div>
      )}

      {blockingItems.length > 0 && (
        <div className="dash-card border-amber-200 bg-amber-50/50" data-testid="ops-actionable-blockers">
          <h3 className="font-semibold text-amber-950 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Points réels à traiter
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {blockingItems.map(([key, item]) => (
              <div key={key} className="rounded-lg bg-white border border-amber-100 p-3" data-testid={`ops-blocker-${key}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-sm font-bold text-slate-800">{item.description}</p>
                  <StatusPill status={item.status} testid={`ops-blocker-${key}-status`} />
                </div>
                <p className="text-xs text-slate-600">{item.evidence}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HEALTH CHECKS */}
      <div className="dash-card">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <HeartPulse className="w-4 h-4" /> Health checks utiles
        </h3>
        {health?.checks && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(health.checks).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2 px-3 rounded bg-slate-50 border border-slate-100">
                <div>
                  <p className="text-sm font-medium text-slate-700">{k.replace(/_/g, " ")}</p>
                  <p className="text-xs text-slate-500">{v.detail}</p>
                </div>
                <StatusPill status={v.status} testid={`ops-health-check-${k}-status`} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 15 PREUVES SECURITE */}
      <div className="dash-card">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4" /> Preuves de securite audit-ready (Annexe 1 DPA)
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          {proofs?.controller_instruction}
        </p>
        {proofs?.proofs && (
          <div className="space-y-2">
            {Object.entries(proofs.proofs).map(([k, v]) => (
              <div key={k} className="flex items-start justify-between py-2 px-3 rounded bg-slate-50 border border-slate-100">
                <div className="flex-1 pr-4">
                  <p className="text-sm font-medium text-slate-700">{v.description}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{v.evidence}</p>
                </div>
                <StatusPill status={v.status} testid={`ops-proof-${k}-status`} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* BACKUPS */}
      <div className="dash-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Cloud className="w-4 h-4" /> Sauvegardes
          </h3>
          <button
            data-testid="ops-backup-run-btn"
            onClick={runBackupNow}
            disabled={!backups?.configured}
            className="btn-primary text-xs disabled:opacity-50"
          >
            Lancer un backup maintenant
          </button>
        </div>
        {!backups?.configured && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 mb-3">
            <p className="font-semibold mb-1">Backup non configure</p>
            <p className="text-xs mb-1">Variables manquantes dans backend/.env :</p>
            <p className="text-xs font-mono">{(backups?.missing_vars || []).join(", ")}</p>
            <p className="text-xs mt-2">{backups?.setup_guide}</p>
          </div>
        )}
        {backups?.last_success && (
          <div className="text-xs text-slate-600 mb-3">
            Dernier backup reussi : <b>{new Date(backups.last_success.finished_at).toLocaleString()}</b> — {backups.last_success.size_mb} MB
          </div>
        )}
        {backups?.recent_runs?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-xs">
              <thead>
                <tr className="text-left border-b border-slate-200">
                  <th className="py-2">Date</th>
                  <th className="py-2">Statut</th>
                  <th className="py-2">Taille</th>
                  <th className="py-2">Duree</th>
                </tr>
              </thead>
              <tbody>
                {backups.recent_runs.slice(0, 10).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2">{r.started_at?.substring(0, 19)}</td>
                    <td><StatusPill status={r.status === "success" ? "ok" : "ko"} testid={`ops-backup-run-${i}-status`} /></td>
                    <td>{r.size_mb ? `${r.size_mb} MB` : "—"}</td>
                    <td>{r.duration_seconds ? `${r.duration_seconds}s` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}

function LiveFact({ label, value, detail, testid }) {
  return (
    <div className="rounded-lg border border-emerald-100 bg-white p-3" data-testid={testid}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-2xl font-bold text-slate-900" data-testid={`${testid}-value`}>{value}</p>
      {detail && <p className="text-xs text-slate-500" data-testid={`${testid}-detail`}>{detail}</p>}
    </div>
  );
}
